// apps/client/src/engine/renderer.ts — WebGL2, DPR ≤ tuning.render.maxDpr, dynamic resolution, GTAO composer on high (look-demo main.ts composerFor)
import { tuning } from '@whiskerton/content';
import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { IS_DEV, IS_E2E } from '../boot/env';
import { recordPerfSample, toPerfSample } from './perf';
import { QUALITY, type QualityLevel } from './quality';
import { installToyShading } from './shader/toyShading';
import { patchMaterial, worldUniforms } from './shader/worldPatch';

/** Contract change 33: step down when the mean frame misses the target by dynResDownRatio (protects minFps before it is crossed);
 *  propose a step up at or under target × dynResUpRatio. The renderer applies an up-step only after dynResUpWindows good windows. */
export function nextScale(scale: number, frameMs: number): number {
  const r = tuning.render, target = 1000 / r.targetFps;
  if (frameMs > target * r.dynResDownRatio) return Math.max(r.dynResMin, +(scale - r.dynResStep).toFixed(3));
  if (frameMs <= target * r.dynResUpRatio) return Math.min(1, +(scale + r.dynResStep).toFixed(3));
  return scale;
}
/** rev 5 item 21: `calls`/`triangles` are the whole frame (shadow maps, GTAO's normal/depth re-render and its full-screen passes);
 *  `sceneCalls` is the main scene pass alone (with its shadow map), the number the §8.7 draw-call budget means, at both qualities. */
export interface RenderStats { fps: number; frameMs: number; calls: number; sceneCalls: number; triangles: number; geometries: number; textures: number;
  scale: number; pixelRatio: number; quality: QualityLevel }
/** The part of WebGLRenderer.info the counter needs (structural, so perf.sample.test.ts passes a fake). */
export interface FrameInfo { autoReset: boolean; reset(): void; readonly render: { calls: number; triangles: number } }
/** One whole frame. three.js resets `info` at the start of every renderer.render() while `autoReset` is on, and the composer
 *  calls render() once per pass, so after composer.render() `info` would hold only OutputPass's quad. Turn auto reset off,
 *  reset once, run the frame, and measure the main scene pass on its own through `scene(draw)`. */
export function countFrame(info: FrameInfo, frame: (scene: (draw: () => void) => void) => void): { calls: number; triangles: number; sceneCalls: number } {
  info.autoReset = false;
  info.reset();
  let sceneCalls = 0;
  frame((draw) => { const before = info.render.calls; draw(); sceneCalls += info.render.calls - before; });
  return { calls: info.render.calls, triangles: info.render.triangles, sceneCalls };
}
/** The composer's RenderPass, measured on its own: the scene as drawn once, including its shadow-map pass. */
class MeasuredRenderPass extends RenderPass {
  measure: (draw: () => void) => void = (draw) => draw();
  override render(...args: Parameters<RenderPass['render']>): void { this.measure(() => super.render(...args)); }
}
export interface Renderer {
  readonly renderer: THREE.WebGLRenderer;
  render(scene: THREE.Scene, camera: THREE.Camera): void; resize(): void; setQuality(q: QualityLevel): void; stats(): RenderStats; dispose(): void;
}
export function createRenderer(canvas: HTMLCanvasElement, quality: QualityLevel): Renderer {
  installToyShading();                                 // the soft toy finish in three's lighting chunks, before any program compiles
  // stencil: a resident's doll marks where it shows, so their silhouette (engine/silhouette.ts) draws only where something hides them.
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance', stencil: true });
  // renderer.info resets on every render() call, so after the composer it would describe only its last full-screen pass (calls 1,
  // tris 1). countFrame resets it once per frame instead and measures the scene pass (shadows included) on its own: `sceneCalls` is
  // the number the draw-call budget means (§8.7), `calls` the whole frame with whatever post-processing follows (rev 5 item 21).
  renderer.info.autoReset = false;
  let level: QualityLevel = quality, scenePass: MeasuredRenderPass | null = null;
  let counts = { calls: 0, triangles: 0, sceneCalls: 0 };
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.shadowMap.type = THREE.PCFShadowMap;   // three r186 removed PCFSoftShadowMap (it warns and falls back to this); soft via shadow.radius
  // Once a frame, by the scene pass: every renderer.render() of a lit scene would redraw the shadow map, and GTAO's normal/depth
  // prepass renders the scene again (M2 playtest: a second full shadow pass on high).
  renderer.shadowMap.autoUpdate = false;
  // E2E builds run on SwiftShader: DPR 1 and the lowest scale from the first frame, so the headless sim isn't starved (M0-T20).
  let q = QUALITY[quality], scale = IS_E2E ? tuning.render.dynResMin : 1, composer: EffectComposer | null = null;
  let composerFor: THREE.Scene | null = null, composerCam: THREE.Camera | null = null;
  let frames = 0, windowStart = performance.now(), fps = 0, frameMs = 0, lastFrame = performance.now(), slowSum = 0, goodWindows = 0;
  const apply = () => {
    renderer.setPixelRatio(Math.min(devicePixelRatio, IS_E2E ? 1 : q.maxDpr) * scale);
    renderer.setSize(innerWidth, innerHeight, false);
    renderer.shadowMap.enabled = q.shadows;
    // The ground map's soft shade stands in for shadows on low; with shadow maps it only deepens them a little (light and look).
    worldUniforms.uGroundShadeK.value = q.shadows ? tuning.light.ground.shade.withShadows : 1;
    worldUniforms.uGroundSunOn.value = q.shadows ? 0 : 1;            // …and with none, the sun's shadows are painted on the ground
    composer?.setPixelRatio(renderer.getPixelRatio());   // the composer keeps its own copy, so dynamic resolution reaches its targets
    composer?.setSize(innerWidth, innerHeight);
  };
  apply();
  const dropComposer = () => {
    if (composer) { for (const p of composer.passes) p.dispose(); composer.dispose(); }
    composer = null; composerFor = null; composerCam = null; scenePass = null;
  };
  const ensureComposer = (scene: THREE.Scene, camera: THREE.Camera) => {
    if (!q.gtao) { dropComposer(); return; }
    if (composer && composerFor === scene && composerCam === camera) return;
    dropComposer();
    // The composer's own targets keep a stencil too (EffectComposer's default has none), for the silhouettes drawn in its scene pass.
    composer = new EffectComposer(renderer, new THREE.WebGLRenderTarget(innerWidth * renderer.getPixelRatio(), innerHeight * renderer.getPixelRatio(),
      { type: THREE.HalfFloatType, stencilBuffer: true }));
    scenePass = new MeasuredRenderPass(scene, camera);
    composer.addPass(scenePass);
    const ao = new GTAOPass(scene, camera as THREE.PerspectiveCamera, innerWidth, innerHeight);
    // GTAOPass renders its normal + depth buffer with scene.overrideMaterial = its MeshNormalMaterial, which skips our per-material
    // onBeforeCompile. Patch it with the same curve/horizon inject, or AO is computed on unbent geometry (offset smears at mid/far
    // range, clipped instances still occluding). If this three version hides the material, run without GTAO rather than draw it wrong.
    const normalMat = (ao as unknown as { normalMaterial?: THREE.Material }).normalMaterial;
    if (!normalMat) { if (IS_DEV) console.warn('GTAOPass.normalMaterial missing; GTAO off'); dropComposer(); q = { ...q, gtao: false }; return; }
    patchMaterial(normalMat);
    ao.updateGtaoMaterial({ radius: 0.6, distanceExponent: 1.6, thickness: 1.2, scale: 1.1, samples: 12 });
    ao.blendIntensity = 0.85;
    // Sprites (smoke, sparkles) and contact blobs must not write into the occlusion prepass, or they read as dark cards (look-demo
    // main.ts); nor may a faded object's ghost (light and look: the prepass works out the occlusion behind it) or a resident's
    // silhouette. three already hides points and lines there; extend its hooks rather than replace them.
    const hooks = ao as unknown as { _overrideVisibility?: () => void; _restoreVisibility?: () => void };
    const baseHide = hooks._overrideVisibility?.bind(ao), baseShow = hooks._restoreVisibility?.bind(ao), hidden: THREE.Object3D[] = [];
    if (baseHide && baseShow) {
      hooks._overrideVisibility = () => {
        baseHide();
        scene.traverse((o) => {
          if (o.visible && ((o as THREE.Sprite).isSprite || o.userData.blob || o.userData.ghost || o.userData.silhouette)) { o.visible = false; hidden.push(o); }
        });
      };
      hooks._restoreVisibility = () => { for (const o of hidden) o.visible = true; hidden.length = 0; baseShow(); };
    }
    composer.addPass(ao);
    composer.addPass(new OutputPass());
    composerFor = scene; composerCam = camera;
  };
  const stats = (): RenderStats => ({ fps, frameMs, calls: counts.calls, sceneCalls: counts.sceneCalls, triangles: counts.triangles,
    geometries: renderer.info.memory.geometries, textures: renderer.info.memory.textures, scale, pixelRatio: renderer.getPixelRatio(), quality: level });
  return {
    renderer,
    render(scene, camera) {
      const t = performance.now();
      let sampleDue = false;
      frameMs = t - lastFrame; lastFrame = t; slowSum += frameMs; frames++;
      if (t - windowStart >= 1000) {                       // adjust resolution once per second from the mean frame time
        sampleDue = true;
        fps = (frames * 1000) / (t - windowStart);
        let next = nextScale(scale, slowSum / frames);
        if (next > scale) { goodWindows++; if (goodWindows < tuning.render.dynResUpWindows) next = scale; else goodWindows = 0; } else goodWindows = 0;
        if (next !== scale) { scale = Math.min(next, q.maxScale); apply(); }
        frames = 0; slowSum = 0; windowStart = t;
      }
      ensureComposer(scene, camera);
      renderer.shadowMap.needsUpdate = true;
      counts = countFrame(renderer.info, (sceneDraw) => {
        const pass = scenePass;                            // narrowed: ensureComposer may have dropped the composer (no GTAO)
        if (composer && pass) { pass.measure = sceneDraw; composer.render(); }
        else sceneDraw(() => renderer.render(scene, camera));
      });
      if (sampleDue) recordPerfSample(toPerfSample(stats(), t));   // once per second, overlay shown or not (rev 5 item 21)
    },
    resize: apply,
    setQuality(l) { level = l; q = QUALITY[l]; dropComposer(); apply(); },
    stats,
    dispose() { dropComposer(); renderer.dispose(); },
  };
}
